import React, { useRef, useState, useCallback, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { apiFetch } from '../../lib/apiFetch';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../lib/apiConfig';

interface UserAvatarProps {
  /** Display name used for initials fallback */
  name: string;
  /** Real photo URL from Supabase Storage (or null/undefined for initials) */
  fotoUrl?: string | null;
  /** Avatar diameter in pixels. Default: 40 */
  size?: number;
  /** When true, shows a camera overlay on hover and triggers upload on click */
  editable?: boolean;
  /** Called with the new public URL after a successful upload */
  onPhotoUpdated?: (newUrl: string) => void;
}

const Wrapper = styled.div<{ $size: number; $editable: boolean }>`
  width: ${p => p.$size}px;
  height: ${p => p.$size}px;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
  cursor: ${p => (p.$editable ? 'pointer' : 'default')};
`;

const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Initials = styled.div<{ $size: number; $bg: string }>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.$bg};
  color: white;
  font-size: ${p => Math.round(p.$size * 0.36)}px;
  font-weight: 700;
  letter-spacing: 0.5px;
  user-select: none;
  line-height: 1;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;

  ${Wrapper}:hover & {
    opacity: 1;
  }
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: white;
  border-radius: 50%;
  animation: avatarSpin 0.8s linear infinite;

  @keyframes avatarSpin {
    to { transform: rotate(360deg); }
  }
`;

const CameraIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

// ── Crop modal styled components ──────────────────────────────────────────────

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`from { transform: translateY(16px) scale(0.98); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; }`;

const CropOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.18s ease;
`;

const CropModal = styled.div`
  background: white;
  border-radius: 20px;
  padding: 28px 24px 20px;
  width: 340px;
  max-width: 95vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  box-shadow: 0 24px 64px rgba(0,0,0,0.3);
  animation: ${slideUp} 0.22s ease;
`;

const CropTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  align-self: flex-start;
`;

const CropCanvasWrap = styled.div`
  position: relative;
  width: 260px;
  height: 260px;
  border-radius: 50%;
  overflow: hidden;
  background: #f1f5f9;
  cursor: grab;
  touch-action: none;
  user-select: none;
  box-shadow: 0 0 0 3px #e2e8f0;
  &:active { cursor: grabbing; }
`;

const CropHint = styled.p`
  font-size: 12px;
  color: #94a3b8;
  margin: -8px 0 0;
  text-align: center;
`;

const ZoomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const ZoomLabel = styled.span`
  font-size: 12px;
  color: #6b7280;
  flex-shrink: 0;
`;

const ZoomSlider = styled.input`
  flex: 1;
  accent-color: var(--accent, #2563eb);
`;

const CropActions = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  margin-top: 4px;
`;

const CropBtn = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 10px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: filter 0.15s;
  background: ${p => p.$primary ? 'var(--accent, #2563eb)' : '#f1f5f9'};
  color: ${p => p.$primary ? 'white' : '#374151'};
  &:hover { filter: brightness(0.94); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

// ── Helpers ───────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  return name.split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

const AVATAR_PALETTES = [
  ['#2563eb', '#1d4ed8'],
  ['#7c3aed', '#6d28d9'],
  ['#0891b2', '#0e7490'],
  ['#059669', '#047857'],
  ['#0369a1', '#075985'],
  ['#dc2626', '#b91c1c'],
  ['#db2777', '#be185d'],
  ['#0f766e', '#0d6d64'],
];

function getAvatarBg(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  const pair = AVATAR_PALETTES[Math.abs(hash) % AVATAR_PALETTES.length];
  return `linear-gradient(135deg, ${pair[0]} 0%, ${pair[1]} 100%)`;
}

function cropImageToBlob(img: HTMLImageElement, offsetX: number, offsetY: number, zoom: number, outputSize = 400): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = outputSize;
  canvas.height = outputSize;
  const ctx = canvas.getContext('2d')!;

  // offsetX/Y are the top-left of the image in preview-canvas space.
  // The visible crop region is the PREVIEW×PREVIEW square starting at (0,0).
  // Convert back to source image coordinates:
  //   srcX = -offsetX / zoom,  srcSize = PREVIEW / zoom
  const srcX = -offsetX / zoom;
  const srcY = -offsetY / zoom;
  const srcSize = 260 / zoom;  // 260 = PREVIEW size

  ctx.drawImage(img, srcX, srcY, srcSize, srcSize, 0, 0, outputSize, outputSize);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(b => b ? resolve(b) : reject(new Error('Canvas toBlob failed')), 'image/jpeg', 0.92);
  });
}

// ── Crop modal component ──────────────────────────────────────────────────────

interface CropModalProps {
  src: string;
  onConfirm: (blob: Blob) => void;
  onCancel: () => void;
  uploading: boolean;
}

const CropModalComponent: React.FC<CropModalProps> = ({ src, onConfirm, onCancel, uploading }) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const PREVIEW = 260;

  // Load image
  useEffect(() => {
    const img = new Image();
    img.onload = () => { imgRef.current = img; setImgLoaded(true); };
    img.src = src;
  }, [src]);

  const clampOffset = useCallback((ox: number, oy: number, z: number) => {
    if (!imgRef.current) return { x: ox, y: oy };
    const displayW = imgRef.current.naturalWidth * z;
    const displayH = imgRef.current.naturalHeight * z;
    const minX = Math.min(0, PREVIEW - displayW);
    const minY = Math.min(0, PREVIEW - displayH);
    return {
      x: Math.max(minX, Math.min(0, ox)),
      y: Math.max(minY, Math.min(0, oy)),
    };
  }, []);

  // Draw on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !imgLoaded) return;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, PREVIEW, PREVIEW);
    const w = img.naturalWidth * zoom;
    const h = img.naturalHeight * zoom;
    ctx.drawImage(img, offset.x, offset.y, w, h);
  }, [zoom, offset, imgLoaded]);

  // Reset center when image loads
  useEffect(() => {
    if (!imgRef.current || !imgLoaded) return;
    const img = imgRef.current;
    const initialZoom = Math.max(PREVIEW / img.naturalWidth, PREVIEW / img.naturalHeight);
    const z = Math.max(1, initialZoom);
    const ox = (PREVIEW - img.naturalWidth * z) / 2;
    const oy = (PREVIEW - img.naturalHeight * z) / 2;
    setZoom(z);
    setOffset(clampOffset(ox, oy, z));
  }, [imgLoaded, clampOffset]);

  const onPointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragging.current = true;
    lastPos.current = { x: e.clientX, y: e.clientY };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;
    lastPos.current = { x: e.clientX, y: e.clientY };
    setOffset(prev => clampOffset(prev.x + dx, prev.y + dy, zoom));
  };

  const onPointerUp = () => { dragging.current = false; };

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.05 : 0.05;
    setZoom(prev => {
      const next = Math.max(0.5, Math.min(5, prev + delta));
      setOffset(o => clampOffset(o.x, o.y, next));
      return next;
    });
  };

  const handleZoomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = parseFloat(e.target.value);
    setZoom(next);
    setOffset(o => clampOffset(o.x, o.y, next));
  };

  const handleConfirm = async () => {
    if (!imgRef.current) return;
    const blob = await cropImageToBlob(imgRef.current, offset.x, offset.y, zoom, 400);
    onConfirm(blob);
  };

  const minZoom = imgRef.current
    ? Math.max(PREVIEW / imgRef.current.naturalWidth, PREVIEW / imgRef.current.naturalHeight)
    : 0.5;

  return (
    <CropOverlay onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <CropModal onClick={e => e.stopPropagation()}>
        <CropTitle>Ajustar foto de perfil</CropTitle>
        <CropCanvasWrap
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          onWheel={onWheel}
        >
          <canvas ref={canvasRef} width={PREVIEW} height={PREVIEW} style={{ display: 'block', width: PREVIEW, height: PREVIEW }} />
        </CropCanvasWrap>
        <CropHint>Arraste para reposicionar · Scroll ou deslize para zoom</CropHint>
        <ZoomRow>
          <ZoomLabel>🔍</ZoomLabel>
          <ZoomSlider
            type="range"
            min={minZoom}
            max={5}
            step={0.01}
            value={zoom}
            onChange={handleZoomChange}
          />
        </ZoomRow>
        <CropActions>
          <CropBtn onClick={onCancel} disabled={uploading}>Cancelar</CropBtn>
          <CropBtn $primary onClick={handleConfirm} disabled={uploading || !imgLoaded}>
            {uploading ? 'Enviando…' : 'Usar foto'}
          </CropBtn>
        </CropActions>
      </CropModal>
    </CropOverlay>
  );
};

// ── Main UserAvatar component ─────────────────────────────────────────────────

const UserAvatar: React.FC<UserAvatarProps> = ({
  name,
  fotoUrl,
  size = 40,
  editable = false,
  onPhotoUpdated,
}) => {
  const { user, session } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [localUrl, setLocalUrl] = useState<string | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);

  const displayUrl = localUrl || fotoUrl || null;

  const handleClick = () => {
    if (editable && !uploading) fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    const ALLOWED = ['image/jpeg', 'image/png', 'image/webp'];
    if (!ALLOWED.includes(file.type)) {
      alert('Selecione uma imagem JPG, PNG ou WebP.');
      return;
    }

    const reader = new FileReader();
    reader.onload = ev => setCropSrc(ev.target?.result as string);
    reader.readAsDataURL(file);

    // Reset input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCropConfirm = async (blob: Blob) => {
    if (!user || !session?.access_token) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('avatar', blob, 'avatar.jpg');

      const res = await apiFetch(`${API_BASE_URL}/api/v1/me/avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Upload failed');
      }

      const { data } = await res.json();
      const freshUrl = data.foto_url;
      setLocalUrl(freshUrl);
      onPhotoUpdated?.(freshUrl);
      setCropSrc(null);
    } catch (err) {
      console.error('Erro ao fazer upload da foto:', err);
      alert('Erro ao fazer upload. Tente novamente.');
    } finally {
      setUploading(false);
    }
  };

  const handleCropCancel = () => setCropSrc(null);

  return (
    <>
      <Wrapper $size={size} $editable={editable} onClick={handleClick}>
        {displayUrl ? (
          <Img src={displayUrl} alt={name} onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }} />
        ) : (
          <Initials $size={size} $bg={getAvatarBg(name || 'U')}>{getInitials(name || 'U')}</Initials>
        )}

        {editable && (
          <>
            <Overlay>
              {uploading ? <Spinner /> : <CameraIcon />}
            </Overlay>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </>
        )}
      </Wrapper>

      {cropSrc && (
        <CropModalComponent
          src={cropSrc}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
          uploading={uploading}
        />
      )}
    </>
  );
};

export default UserAvatar;
